import { BaseService } from '@/modules/core/app/services/abstracts/BaseService';
import ModalService from '@/modules/core/ui/services/ModalService';
import { Component } from 'vue';
import BaseFlowModal from '@/modules/core/form/components/BaseFlowModal.vue';
import { StripeCheckoutSession } from '@/modules/billing/models/stripe/StripeCheckoutSession';
import DocumentUIService from '@/modules/core/app/services/DocumentUIService';
import SelfServeCheckoutSessionFlow_1 from '@/modules/billing/components/flow/standardplan/SelfServeCheckoutSessionFlow_1.vue';
import { CheckoutSessionFlowService } from '@/modules/billing/services/CheckoutSessionFlowService';
import ProtectionMandateCheckoutSessionFlow from '@/modules/billing/components/flow/standardplan/ProtectionMandateCheckoutSessionFlow.vue';
import { CheckoutSessionCart } from '@/modules/billing/models/CheckoutSessionCart';
import { BillingPlanId } from '@/modules/billing/constants/BillingPlanId';
import { CurrentEstate } from '@/modules/estate/models/CurrentEstate';
import { getters } from '@/modules/core/app/helpers/store';
import { CheckoutSessionModalParams } from '@/modules/billing/services/CheckoutSessionModalParams';
import EventBus from '/@/modules/core/app/helpers/EventBus';
import { BillingEvent } from '/@/modules/billing/constants/BillingEvent';
import { PaywallPaymentModalQuitEventTracking } from '@/modules/auth/models/segmentevents/PaywallPaymentModalQuitEventTracking';

export class SelfServeCheckoutSessionUIService extends BaseService {
  showStandardPlanModal(params: CheckoutSessionModalParams): void {
    const currentEstate: CurrentEstate | null = getters.auth.getUser()?.currentEstate ?? null;

    this.openModal(
      params,
      SelfServeCheckoutSessionFlow_1,
      currentEstate?.billing?.billingPlanMapping?.planId ?? BillingPlanId.INDIVIDUAL_STANDARD_PLAN
    );
  }

  showIndividualBasicPlanModal(params: CheckoutSessionModalParams): void {
    this.openModal(params, SelfServeCheckoutSessionFlow_1, BillingPlanId.INDIVIDUAL_BASIC_PLAN);
  }

  showIndividualStandardPlanModal(params: CheckoutSessionModalParams): void {
    this.openModal(params, SelfServeCheckoutSessionFlow_1, BillingPlanId.INDIVIDUAL_STANDARD_PLAN);
  }

  showCouplesBasicPlanModal(params: CheckoutSessionModalParams): void {
    this.openModal(params, SelfServeCheckoutSessionFlow_1, BillingPlanId.COUPLES_BASIC_PLAN);
  }

  showBasicPlanModal(params: CheckoutSessionModalParams): void {
    this.openModal(
      params,
      SelfServeCheckoutSessionFlow_1,
      getters.auth.getUser()?.currentEstate?.billing?.billingPlanMapping?.planId ??
        BillingPlanId.INDIVIDUAL_BASIC_PLAN
    );
  }

  showSelfServePlanModal(params: CheckoutSessionModalParams): void {
    if (getters.auth.getUser()?.currentEstate?.isDigitalVaultPlan) {
      this.showModalForDigitalVaultPlan(params);
      return;
    }

    if (getters.auth.getUser()?.currentEstate?.isBasicPlan) {
      this.showBasicPlanModal(params);
      return;
    }

    this.showStandardPlanModal(params);
  }

  private showModalForDigitalVaultPlan(params: CheckoutSessionModalParams): void {
    if (getters.auth.getUser()?.currentEstate?.paywall?.hasDigitalVaultDocBuilderBasic) {
      this.showIndividualBasicPlanModal(params);
      return;
    }

    this.showIndividualStandardPlanModal(params);
  }

  showProtectionMandateModal(params: CheckoutSessionModalParams): void {
    this.openModal(params, ProtectionMandateCheckoutSessionFlow);
  }

  private openModal(
    params: CheckoutSessionModalParams,
    firstElement: Component,
    billingPlanId?: BillingPlanId
  ): void {
    ModalService.show({
      parent: params.parent,
      component: BaseFlowModal,
      props: {
        customForm: new CheckoutSessionCart({ successUrl: params.successUrl }),
        service: new CheckoutSessionFlowService(firstElement, billingPlanId, params.text),
        postSubmit: (session: StripeCheckoutSession): void => {
          DocumentUIService.openUrl(session.url, '_self');
        },
        width: '669px',
        closeInModal: true,
        hideCancel: true
      },
      fullScreen: false,
      width: '1088px',
      hasModalCard: true,
      canCancel: ['escape', 'outside', 'button'],
      onCancel: (): void => {
        EventBus.signal(BillingEvent.ON_PAYWALL_CLOSED);
        PaywallPaymentModalQuitEventTracking.dispatch(DocumentUIService.getUrl());
      }
    });
  }
}

export default new SelfServeCheckoutSessionUIService();
